import { Injectable } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { BehaviorSubject, forkJoin, Observable, take } from 'rxjs';
import { AccountAdminService } from '../services/account-admin.service';
import { CaseAccess } from '../models/ui/case-access.model';
import { LexbeCaseToCaseAccessMapper } from '../models/mappers/lexbe-case-to-case-access.mapper';
import { AccountUser } from '../models/account-user.model';
import { LexbeCase } from '../models/case.model';
import { orderBy } from 'lodash-es';
import { CasesService } from '../services/cases.service';
import { CaseAdminSdk } from '../sdks/case-admin.sdk';

const mapper = new LexbeCaseToCaseAccessMapper();

@Injectable({
	providedIn: 'root'
})
export class CaseAccessManager {
	public caseCollection$: Observable<CaseAccess[]>;
	public activeUserCollection$: Observable<AccountUser[]>;

	private caseCollectionSource: BehaviorSubject<CaseAccess[]> = new BehaviorSubject<CaseAccess[]>([]);
	private activeUserCollectionSource: BehaviorSubject<AccountUser[]> = new BehaviorSubject<AccountUser[]>([]);

	constructor(
		private accountAdminService: AccountAdminService,
		private casesService: CasesService,
		private caseAdminSdk: CaseAdminSdk
		) {
		this.caseCollection$ = this.caseCollectionSource.asObservable();
		this.activeUserCollection$ = this.activeUserCollectionSource.asObservable();
	}

	public getCasesAccess(accountId: string): void {
		forkJoin([
			this.accountAdminService.getAccountUsers(accountId),
			this.accountAdminService.getCases(accountId)
		]).subscribe(([accountUsers, cases]) => {
			const caseAccesses = mapper.mapCollection(cases, accountUsers);
			this.caseCollectionSource.next(caseAccesses);
		});
	}

	public sortCaseAccess(sort: Sort): void {
		if (sort.active && sort.direction !== '') {
			if (sort.active === 'userCount')
				this.caseCollectionSource.pipe(take(1)).subscribe((collection) =>
				{
					this.caseCollectionSource.next(orderBy(collection, (caseAccess: CaseAccess) => { return caseAccess.accessibleUsers.length }, [sort.direction as ('asc' | 'desc')]));
				});
			else
				this.caseCollectionSource.pipe(take(1)).subscribe((collection) => {
					this.caseCollectionSource.next(orderBy(collection, [sort.active], [sort.direction as ('asc' | 'desc')]));
				});
		}
	}

	public updateCaseUsersAccesses(lexbeCase: CaseAccess, allowedUserIds: string[]): Observable<string> {
		return this.caseAdminSdk.updateCaseUsersAccesses(lexbeCase.caseId, allowedUserIds);
	}

	public updateCaseUsersAccessesWithEmployees(caseId: string, allowedUserIds: string[]): Observable<string> {
		return this.caseAdminSdk.updateCaseUsersAccessesWithEmployees(caseId, allowedUserIds);
	}

	public getActiveUsers(accountId: string): void {
		this.accountAdminService.getActiveAccountUsers(accountId).subscribe(data => {
			this.activeUserCollectionSource.next(data);
		});
	}

	public updateAccountUserAccess(accountId: string, userIds: string[]): Observable<string> {
		return this.accountAdminService.updateAccountUserAccess(accountId, userIds);
	}

	public createCase(lexbeCase): Observable<LexbeCase> {
		return this.casesService.addNewCase(lexbeCase);
	}

}
